import { TIME_AXIS_HEIGHT_PX, LAYOUT_PASSES } from "./constants";
import { buildPriceTicks } from "./axes/priceTicks";
import { buildPriceLabels, priceAxisWidthFor, fullPriceWidth } from "./axes/priceAxis";
import { MAIN_PANE_ID } from "./indicators/indicatorTypes";
import { mergeRanges } from "./indicators/valueRange";
import { layoutIndicatorPanes } from "./indicators/indicatorPanes";

/**
 * Splits the chart into the price pane, the oscillator panes under it and
 * the axes, autoscales the price scale to the visible part of the series and
 * the price indicators (smoothed by the animator), lays the oscillator panes
 * out and builds price labels. The price axis is as wide as
 * its longest label or a price at the full precision (E16); when the labels
 * change the width, the layout is repeated once with the new width.
 *
 * @param {object} params
 * @param {{ width: number, height: number }} params.size - CSS px
 * @param {object} params.store
 * @param {object} params.series - mainSeries: the prices autoscale fits
 * @param {object} params.indicators - createIndicators: their values are fitted too
 * @param {object} params.paneStack - places the oscillator panes (D24)
 * @param {object} params.timeScale
 * @param {object} params.priceScale
 * @param {(range: object|null, visibleBars: object) => object|null} params.smoothPriceRange -
 *   animator.smoothPriceRange
 * @param {{ isSnapped: boolean, now: number }} params.frameState - animator.frameState, for the
 *   autoscale of the oscillator panes
 * @param {number} params.priceAxisWidth - width used in the previous frame
 * @param {{ decimals: number, minMove: number }} params.priceFormat
 * @param {(text: string, font: string) => number} params.measure
 * @param {string} params.fontFamily
 * @returns {{
 *   pane: { width: number, height: number }, plotHeight: number, indicatorPanes: object[],
 *   paneArea: { top: number, height: number, contentHeight: number, scrollTop: number },
 *   range: object, priceLabels: object[], priceAxisWidth: number,
 * }} pane: the price pane; plotHeight: all panes above the time axis; indicatorPanes: layoutIndicatorPanes;
 *   paneArea: the area of the oscillator panes under the price pane
 */
export const layoutFrame = ({
  size,
  store,
  series,
  indicators,
  paneStack,
  timeScale,
  priceScale,
  smoothPriceRange,
  frameState,
  priceAxisWidth,
  priceFormat,
  measure,
  fontFamily,
}) => {
  const plotHeight = Math.max(0, size.height - TIME_AXIS_HEIGHT_PX);
  const { mainHeight, areaHeight, contentHeight, scrollTop, slots } = paneStack.arrange(
    plotHeight,
    indicators.oscillators,
  );
  const paneArea = { top: mainHeight, height: areaHeight, contentHeight, scrollTop };
  let axisWidth = priceAxisWidth;
  let layout = null;

  for (let pass = 0; pass < LAYOUT_PASSES; pass += 1) {
    const pane = { width: Math.max(0, size.width - axisWidth), height: mainHeight };

    timeScale.setWidth(pane.width);
    priceScale.setHeight(pane.height);

    const range = timeScale.visibleRange(store.length);

    const visiblePrices = mergeRanges([
      series.priceRange(range.first, range.last),
      indicators.priceRange(MAIN_PANE_ID, range.first, range.last),
    ]);

    priceScale.autoscale(smoothPriceRange(visiblePrices, range));

    const priceTicks = store.length ? buildPriceTicks(priceScale, priceFormat.minMove) : [];
    const priceLabels = buildPriceLabels(priceTicks, priceFormat.decimals, measure, fontFamily);
    const indicatorPanes = layoutIndicatorPanes({
      slots,
      areaTop: mainHeight,
      areaHeight,
      width: pane.width,
      range,
      frameState,
      pairDecimals: priceFormat.decimals,
      measure,
      fontFamily,
    });

    layout = { pane, plotHeight, indicatorPanes, paneArea, range, priceLabels, priceAxisWidth: axisWidth };

    const fullWidth = store.length
      ? fullPriceWidth(priceScale, priceFormat.decimals, measure, fontFamily)
      : 0;
    const allLabels = priceLabels.concat(...indicatorPanes.map((indicatorPane) => indicatorPane.priceLabels));
    const requiredWidth = priceAxisWidthFor(allLabels, fullWidth);

    if (requiredWidth === axisWidth) break;

    axisWidth = requiredWidth;
  }

  return layout;
};
